import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import type { ImagesType, ProjectImageType } from '@/api/media';
import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { env } from '@/constants/env';
import { type Estate, getEstateQueryOptions } from '@/features/estates-property/api/estate';
import { useUpdateEstateThumbnail } from '@/features/estates-property/api/estate-thumbnail';
import { useUpdateProjectThumbnail } from '@/features/project-media/api/update-thumbnail';
import { getProjectQueryOptions, type Project } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';

export type SelectThumbnailModalProps =
  | {
      id: Estate['id'];
      type?: 'estate';
      images: ImagesType[string];
    }
  | {
      id: Project['id'];
      type?: 'project';
      images: ProjectImageType[];
    };

export const SelectThumbnailModal: React.FC<SelectThumbnailModalProps> = ({
  id,
  images,
  type = 'estate',
}) => {
  const { t } = useTranslation();
  const [loading] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const queryClient = useQueryClient();

  const { mutateAsync: updateEstateThumbnail } = useUpdateEstateThumbnail({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('update-estate-details-success'));
        queryClient.invalidateQueries({ queryKey: getEstateQueryOptions(id).queryKey });
      },
    },
  });

  const { mutateAsync: updateProjectThumbnail } = useUpdateProjectThumbnail({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('update-project-success'));
        queryClient.invalidateQueries({ queryKey: getProjectQueryOptions(id).queryKey });
      },
    },
  });

  const onImageClick = (id: string) => () => setSelectedImage(id);

  const onSubmit = async () => {
    if (type === 'project') {
      await updateProjectThumbnail({ projectId: id, imageId: selectedImage });
    } else {
      await updateEstateThumbnail({ estateId: id, imageId: selectedImage });
    }
    closeModal();
  };

  return (
    <ModalBase className="max-w-96">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      )}
      <p>{t('select-thumbnail-photo')}</p>
      <div className="grid max-h-96 max-w-lg grid-cols-2 gap-1 overflow-y-scroll rounded">
        {images.map((image) => (
          <button
            type="button"
            key={image.id}
            className={cn(
              'h-32 cursor-pointer overflow-hidden rounded',
              selectedImage === image.id && 'border-4 border-primary-light'
            )}
            onClick={onImageClick(image.id)}
          >
            <img
              className="size-full object-cover"
              src={`${env.API_URL}/images/${image.mediaId}?size=thumbnail200`}
              alt={image.fileName || ''}
            />
          </button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={closeModal}>
          {t('cancel')}
        </Button>
        <Button disabled={!selectedImage} onClick={onSubmit}>
          {t('submit')}
        </Button>
      </div>
    </ModalBase>
  );
};
